import React, { MouseEventHandler, useEffect, useRef } from 'react'
import noop from 'lib/function/noop'
import cn from 'clsx'
import styled from 'styled-components'
import zIndex from 'styles/tools/z-index'
import useClickOutside from 'hooks/useClickOutside'
import { autoUpdate, flip, offset, Placement, shift, size, useFloating } from '@floating-ui/react-dom'
import { shallowEqual } from 'react-redux'
import { themeClassName } from 'lib/theme/themeUtils'

const DropdownWrapper = styled.div`
  position: absolute;
  z-index: ${zIndex.menu};
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s;
  visibility: hidden;

  &.visible {
    opacity: 1;
    pointer-events: auto;
    visibility: visible;
  }
`

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  onClose?: MouseEventHandler;
  open?: boolean;
  /**
   * This is the ref to the element that is triggering the dropdown
   * This needs to be passed so we can prevent the dropdown from closing
   * when we don't want it to. e.g. event bubbling up from the trigger
   */
  triggerRef: React.RefObject<HTMLElement>;
  /**
   * Use this ref to 'anchor' the dropdown to it
   * i.e. it put the drop down underneath and aligned to the anchor
   *
   * If not defined, will default to the trigger ref
   */
  anchorRef?: React.RefObject<HTMLElement>;
  asComponent?: keyof JSX.IntrinsicElements;
  position?: Placement;
  width?: string | number | null;
  offsetSize?: number;
  floatingStrategy?: 'absolute' | 'fixed',
  disableFlip?: boolean;
  crossAxisShift?: boolean;
  /**
   * Will automatically limit the size of the element
   *
   * Autosize === true
   * This will ensure the dropdown will not be larger than its' clipping context
   *
   * Autosize === reference
   * This will make the dropdowns size match the reference elements (trigger ref) width
   * Often used for say, select dropdowns where the dropdown should be the size of the select
   */
  autoSize?: boolean | 'reference';
}

/**
 * Generic "dropdown" container that fires an on close when something other than
 * itself is clicked.
 */
function Dropdown(props: Props) {
  const {
    onClose = noop,
    asComponent,
    className,
    open = false,
    children,
    position = 'bottom',
    triggerRef,
    width,
    offsetSize = 8,
    floatingStrategy = 'fixed',
    disableFlip,
    crossAxisShift = false,
    autoSize,
    anchorRef,
    ...rest
  } = props

  const dropdownRef = useRef<HTMLDivElement | null>(null)

  const { x, y, reference, floating, strategy, update } = useFloating({
    placement: position,
    strategy: floatingStrategy,
    middleware: [
      offset(offsetSize),
      ...(disableFlip ? [] : [flip()]),
      shift({ padding: 16, crossAxis: crossAxisShift }),
      ...(autoSize ? [size({
        padding: offsetSize,
        apply({ availableWidth, availableHeight, elements, rects }) {
          if (autoSize === 'reference') {
            Object.assign(elements.floating.style, {
              width: `${rects.reference.width}px`,
            })
          } else {
            Object.assign(elements.floating.style, {
              maxWidth: `${availableWidth}px`,
              maxHeight: `${availableHeight}px`,
            })
          }
        },
      })] : []),
    ],
  })

  useEffect(() => {
    if (open && triggerRef.current && dropdownRef.current) {
      return autoUpdate(triggerRef.current, dropdownRef.current, update)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => {
    if (anchorRef?.current ?? triggerRef.current) {
      reference(anchorRef?.current ?? triggerRef.current)
    }
  }, [reference, triggerRef, anchorRef])

  const closeOnClick = (e) => {
    if (// also don't close if they've clicked on our trigger element
      !triggerRef.current?.contains(e.target as HTMLElement) &&
      // or the triggerRef is still active e.g. search input
      !triggerRef.current?.contains(document.activeElement)
    ) {
      onClose(e)
    }
  }

  useClickOutside(dropdownRef, closeOnClick, open)

  return (
    <DropdownWrapper
      {...rest}
      as={asComponent}
      ref={(element) => {
        dropdownRef.current = element
        floating(element)
      }}
      style={{
        position: strategy,
        top: y ?? 0,
        left: x ?? 0,
        width: width || 'max-content',
      }}
      className={cn(
        className,
        themeClassName('default'),
        { visible: open },
      )
      }
    >
      {children}
    </DropdownWrapper>
  )
}

export default React.memo(Dropdown, (prevProps, nextProps) => {
  if (!prevProps.open && !nextProps.open) {
    return true
  }
  return shallowEqual(prevProps, nextProps)
})
